import { useState } from 'react';
import { Button, H2, Paragraph, Spinner, Text, XStack, YStack } from 'tamagui';
import { useDeleteSenderMemoryEntry, useSenderMemoryList } from '../mail/hooks';

const PAGE_SIZE = 30;

export function SenderMemorySection() {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useSenderMemoryList(page, PAGE_SIZE);
  const { mutate: deleteEntry, isPending: isDeleting } = useDeleteSenderMemoryEntry();

  if (isLoading) return <Spinner />;
  if (error) return <Paragraph color="$red10">Fehler: {String(error)}</Paragraph>;
  if (!data) return null;

  const { entries, total } = data;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <YStack gap="$3">
      <XStack justifyContent="space-between" alignItems="center">
        <H2>Mail-Lernspeicher</H2>
        <Text fontSize="$2" color="$color10">
          {total} {total === 1 ? 'Eintrag' : 'Einträge'}
        </Text>
      </XStack>

      {entries.length === 0 ? (
        <Paragraph color="$color10">
          Noch nichts gelernt — wird beim Bestätigen von Vorschlägen automatisch gefüllt.
        </Paragraph>
      ) : (
        <YStack gap="$2">
          {entries.map((entry) => (
            <XStack
              key={entry.id}
              padding="$3"
              borderRadius="$3"
              borderWidth={1}
              borderColor="$borderColor"
              gap="$3"
              alignItems="center"
            >
              <YStack flex={1} gap="$1">
                <Text fontSize="$4" fontWeight="600">{entry.senderAddr}</Text>
                <Text fontSize="$3" color="$color11">→ {entry.tagName}</Text>
                <Text fontSize="$2" color="$color10">
                  {entry.confirmCount} bestätigt · {entry.removeCount} entfernt · zuletzt {formatDate(entry.lastSeenAt)}
                </Text>
              </YStack>
              <Button
                size="$2"
                variant="outlined"
                color="$red10"
                disabled={isDeleting}
                onPress={() => deleteEntry(entry.id)}
              >
                Entfernen
              </Button>
            </XStack>
          ))}
        </YStack>
      )}

      {totalPages > 1 && (
        <XStack gap="$2" alignItems="center" justifyContent="flex-end">
          <Button size="$2" disabled={page <= 1} onPress={() => setPage((p) => Math.max(1, p - 1))}>
            ←
          </Button>
          <Text fontSize="$2">{page} / {totalPages}</Text>
          <Button size="$2" disabled={page >= totalPages} onPress={() => setPage((p) => p + 1)}>
            →
          </Button>
        </XStack>
      )}
    </YStack>
  );
}

function formatDate(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
